import { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Crown, X } from 'lucide-react';
import { useAuthStore } from '@/platform/stores/useAuthStore';

/**
 * Phase 2 cross-cutting polish — toast that fires once when a Stripe webhook
 * lands and the auth-store sees a FREE → PREMIUM/PROFESSIONAL transition.
 * The store sets `pendingUpgradeToast`; this component reads, displays for
 * ~5s, then clears.
 */
export function TierUpgradeToast() {
  const pending = useAuthStore((state) => state.pendingUpgradeToast);
  const dismiss = useAuthStore((state) => state.dismissUpgradeToast);

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => dismiss(), 5000);
    return () => window.clearTimeout(timer);
  }, [pending, dismiss]);

  return (
    <AnimatePresence>
      {pending ? (
        <motion.div
          key="tier-upgrade-toast"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25 }}
          className="pointer-events-auto fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-ink bg-ink px-5 py-3 text-sm text-paper shadow-lg"
        >
          <Crown className="h-4 w-4" />
          <span>You&apos;re on {pending === 'PROFESSIONAL' ? 'Pro' : 'Premium'}!</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={dismiss}
            className="ml-1 text-paper/80 hover:text-paper"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default TierUpgradeToast;
